import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';

const Dashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('portfolios');
  const [portfolioData, setPortfolioData] = useState(null);
  const [portfolios, setPortfolios] = useState([]);
  const [batches, setBatches] = useState([]);
  const [batchIndexes, setBatchIndexes] = useState({}); // { portfolioId: currentBatchIndex }
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [deleteModal, setDeleteModal] = useState({ show: false, portfolio: null });
  const [deleting, setDeleting] = useState(false);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [batchProgress, setBatchProgress] = useState({}); // { batchId: progressData }

  const fetchPortfolioData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/portfolios');
      setPortfolioData(response.data);
      setPortfolios(response.data.portfolios || []);
    } catch (error) {
      console.error('Error fetching portfolio data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await api.get('/simulation-batches');
      const fetchedBatches = response.data.batches || [];
      setBatches(fetchedBatches);

      // Initialize batch indexes (first batch for each portfolio)
      const indexes = {};
      portfolios.forEach(portfolio => {
        indexes[portfolio.id] = 0;
      });
      setBatchIndexes(indexes);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const handleBatchNavigate = (portfolioId, direction) => {
    const portfolioBatches = batches.filter(b => b.portfolio_id === portfolioId);
    if (portfolioBatches.length === 0) return;

    setBatchIndexes(prev => {
      const currentIndex = prev[portfolioId] || 0;
      let newIndex = currentIndex + direction;

      // Wrap around
      if (newIndex < 0) newIndex = portfolioBatches.length - 1;
      if (newIndex >= portfolioBatches.length) newIndex = 0;

      return { ...prev, [portfolioId]: newIndex };
    });
  };

  const handlePortfolioSelect = (portfolio) => {
    setSelectedPortfolio(portfolio);
  };

  const handleDeleteClick = (portfolio) => {
    setDeleteModal({ show: true, portfolio });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.portfolio) return;

    setDeleting(true);
    try {
      await api.delete(`/portfolios/${deleteModal.portfolio.id}`);

      // Обновляем список портфелей
      setPortfolios(prev => prev.filter(p => p.id !== deleteModal.portfolio.id));

      // Сбрасываем выбранный портфель, если он был удален
      if (selectedPortfolio && selectedPortfolio.id === deleteModal.portfolio.id) {
        setSelectedPortfolio(null);
      }

      // Показываем сообщение об успехе
      setMessage(`Портфель "${deleteModal.portfolio.name}" успешно удален`);
      setTimeout(() => setMessage(''), 5000);

      // Закрываем модальное окно
      setDeleteModal({ show: false, portfolio: null });
    } catch (error) {
      console.error('Error deleting portfolio:', error);
      setMessage(`Ошибка при удалении портфеля: ${error.response?.data?.detail || error.message}`);
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ show: false, portfolio: null });
  };

  const handleBatchDelete = async (batchId, portfolioId) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту оптимизацию? Все связанные симуляции будут удалены.')) {
      return;
    }

    try {
      await api.delete(`/simulation-batches/${batchId}`);

      // Обновляем список батчей
      setBatches(prev => prev.filter(b => b.id !== batchId));

      // Сбрасываем индекс батча для портфеля
      setBatchIndexes(prev => ({
        ...prev,
        [portfolioId]: 0
      }));

      setMessage('Оптимизация успешно удалена');
      setTimeout(() => setMessage(''), 5000);
    } catch (error) {
      console.error('Error deleting batch:', error);
      setMessage(`Ошибка при удалении: ${error.response?.data?.detail || error.message}`);
      setTimeout(() => setMessage(''), 5000);
    }
  };

  const fetchBatchProgress = async (batchId) => {
    try {
      const response = await api.get(`/simulation-batches/${batchId}/progress`);
      setBatchProgress(prev => ({
        ...prev,
        [batchId]: response.data
      }));
    } catch (error) {
      console.error(`Error fetching progress for batch ${batchId}:`, error);
    }
  };


  useEffect(() => {
    fetchPortfolioData();

    // Показываем сообщение при возврате с создания портфеля/оптимизации
    if (location.state?.message) {
      setMessage(location.state.message);
      setTimeout(() => setMessage(''), 5000);
    }

    // Переключаем вкладку если указано в state
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location]);

  useEffect(() => {
    if (activeTab === 'optimization' && portfolios.length > 0) {
      fetchBatches();
    }
  }, [activeTab, portfolios]);

  // Периодическое обновление прогресса для активных батчей
  useEffect(() => {
    if (activeTab !== 'optimization' || batches.length === 0) {
      return;
    }

    // Загрузить прогресс для всех батчей, которые не завершены
    const activeBatches = batches.filter(b =>
      b.status === 'pending' || b.status === 'running' || b.status === 'sampling'
    );

    if (activeBatches.length === 0) {
      return;
    }

    // Первоначальная загрузка
    activeBatches.forEach(batch => fetchBatchProgress(batch.id));

    // Обновление каждые 2 секунды
    const interval = setInterval(() => {
      activeBatches.forEach(batch => fetchBatchProgress(batch.id));
    }, 2000);

    return () => clearInterval(interval);
  }, [activeTab, batches]);


  return (
    <div className="dashboard">
      <h1>TradeOffLab Dashboard</h1>

      {/* Сообщение об успешном создании */}
      {message && (
        <div className="success-message">
          {message}
        </div>
      )}

      {/* Вкладки */}
      <div className="dashboard-tabs">
        <button
          className={`tab-button ${activeTab === 'portfolios' ? 'active' : ''}`}
          onClick={() => setActiveTab('portfolios')}
        >
          Портфели
        </button>
        <button
          className={`tab-button ${activeTab === 'optimization' ? 'active' : ''}`}
          onClick={() => setActiveTab('optimization')}
        >
          Оптимизация
        </button>
      </div>

      {/* Содержимое вкладок */}
      {activeTab === 'portfolios' && (
        <div className="dashboard-content">
          <div className="portfolio-overview">
            <div className="section-header">
              <h2>Мои портфели</h2>
              <button
                onClick={() => navigate('/create-portfolio')}
                className="create-portfolio-btn"
              >
                + Создать портфель
              </button>
            </div>

          {loading ? (
            <p>Загрузка...</p>
          ) : portfolios.length > 0 ? (
            <div className="portfolios-grid">
              {portfolios.map((portfolio) => (
                <div
                  key={portfolio.id}
                  className={`portfolio-card ${selectedPortfolio?.id === portfolio.id ? 'selected' : ''}`}
                  onClick={() => handlePortfolioSelect(portfolio)}
                >
                  <h3>{portfolio.name}</h3>
                  <div className="portfolio-info">
                    <p><strong>Общая стоимость:</strong> ${portfolio.total_value?.toLocaleString()}</p>
                    <p><strong>Активов:</strong> {portfolio.assets?.length}</p>
                    <p><strong>Создан:</strong> {new Date(portfolio.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="portfolio-actions">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/simulation?portfolioId=${portfolio.id}`);
                      }}
                      className="action-btn simulation-btn"
                    >
                      Симуляция
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(portfolio);
                      }}
                      className="action-btn delete-btn"
                      disabled={loading}
                    >
                      Удалить
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">📊</div>
              <h3>У вас пока нет портфелей</h3>
              <p>Создайте свой первый инвестиционный портфель для анализа и оптимизации</p>
              <button
                onClick={() => navigate('/create-portfolio')}
                className="create-first-portfolio-btn"
              >
                Создать первый портфель
              </button>
            </div>
          )}
        </div>

        <div className="quick-actions">
          <h2>Быстрые действия</h2>

          {selectedPortfolio ? (
            <>
              <div className="selected-portfolio-info">
                <h3>Выбранный портфель</h3>
                <div className="selected-portfolio-details">
                  <p className="portfolio-name">{selectedPortfolio.name}</p>
                  <p className="portfolio-value">
                    ${selectedPortfolio.total_value?.toLocaleString()}
                  </p>
                </div>

                <div className="portfolio-composition">
                  <h4>Состав портфеля:</h4>
                  <div className="assets-list">
                    {selectedPortfolio.assets?.map((asset, index) => (
                      <div key={index} className="asset-item">
                        <span className="asset-symbol">{asset.symbol}</span>
                        <span className="asset-percentage">{asset.percentage}%</span>
                        <span className="asset-amount">${asset.amount?.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="action-buttons">
                <button
                  onClick={() => navigate(`/simulation?portfolioId=${selectedPortfolio.id}`)}
                  className="action-button simulation"
                >
                  🎯 Запустить симуляцию
                </button>
              </div>
            </>
          ) : (
            <div className="no-selection">
              <p>Выберите портфель для анализа</p>
              <button
                className="action-button disabled"
                disabled
              >
                🎯 Запустить симуляцию
              </button>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Вкладка Оптимизация */}
      {activeTab === 'optimization' && (
        <div className="optimization-content">
          <div className="section-header">
            <h2>Оптимизация портфелей</h2>
            <button
              className="create-batch-btn"
              onClick={() => navigate('/optimization/create')}
            >
              + Создать оптимизацию
            </button>
          </div>

          {loading ? (
            <p>Загрузка...</p>
          ) : portfolios.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📊</div>
              <h3>Сначала создайте портфель</h3>
              <p>Для запуска оптимизации нужен хотя бы один портфель</p>
              <button
                onClick={() => setActiveTab('portfolios')}
                className="create-first-portfolio-btn"
              >
                Перейти к портфелям
              </button>
            </div>
          ) : (
            <div className="optimization-grid">
              {portfolios.map((portfolio) => {
                const portfolioBatches = batches.filter(b => b.portfolio_id === portfolio.id);
                const currentBatchIndex = batchIndexes[portfolio.id] || 0;
                const currentBatch = portfolioBatches[currentBatchIndex];

                return (
                  <div key={portfolio.id} className="optimization-row">
                    {/* Карточка портфеля */}
                    <div className="portfolio-card-compact">
                      <h3>{portfolio.name}</h3>
                      <div className="portfolio-info-compact">
                        <p><strong>Стоимость:</strong> ${portfolio.total_value?.toLocaleString()}</p>
                        <p><strong>Активов:</strong> {portfolio.assets?.length}</p>
                        <div className="assets-compact">
                          {portfolio.assets?.map((asset, idx) => (
                            <span key={idx} className="asset-tag">
                              {asset.symbol} {asset.percentage}%
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Карточка батча */}
                    <div className="batch-card">
                      {portfolioBatches.length === 0 ? (
                        <div className="no-batches">
                          <div className="empty-icon-small">
                            <img src="/images/optimization-icon.jpg" alt="Optimization" className="optimization-icon-img" />
                          </div>
                          <p>Нет оптимизаций</p>
                          <button
                            onClick={() => navigate('/optimization/create', { state: { portfolioId: portfolio.id } })}
                            className="create-batch-small-btn"
                          >
                            Создать первую
                          </button>
                        </div>
                      ) : (
                        <>
                          {/* Навигация */}
                          <div className="batch-navigation">
                            <button
                              onClick={() => handleBatchNavigate(portfolio.id, -1)}
                              className="nav-arrow"
                              disabled={portfolioBatches.length <= 1}
                            >
                              ←
                            </button>
                            <span className="batch-counter">
                              Батч #{currentBatchIndex + 1}/{portfolioBatches.length}
                            </span>
                            <button
                              onClick={() => handleBatchNavigate(portfolio.id, 1)}
                              className="nav-arrow"
                              disabled={portfolioBatches.length <= 1}
                            >
                              →
                            </button>
                          </div>

                          {/* Содержимое батча */}
                          <div className="batch-content">
                            <h4 className="batch-name">{currentBatch.name || `Батч ${currentBatch.id.slice(0, 8)}`}</h4>

                            <div className="batch-info">
                              <p>
                                <strong>Статус:</strong>{' '}
                                <span className={`status-badge status-${currentBatch.status}`}>
                                  {currentBatch.status === 'completed' && '✓ Завершен'}
                                  {currentBatch.status === 'in_progress' && '⏳ Выполняется'}
                                  {currentBatch.status === 'pending' && '⏸ Ожидает'}
                                  {currentBatch.status === 'failed' && '✗ Ошибка'}
                                </span>
                              </p>
                              <p><strong>Симуляций:</strong> {currentBatch.total_simulations || 0}</p>
                              <p><strong>Создан:</strong> {new Date(currentBatch.created_at).toLocaleDateString()}</p>
                            </div>

                            {/* Прогресс бар */}
                            {batchProgress[currentBatch.id] && (
                              <div className="batch-progress">
                                <div className="progress-info">
                                  <span>
                                    Завершено: {batchProgress[currentBatch.id].completed}/{batchProgress[currentBatch.id].total}
                                  </span>
                                  <span>{batchProgress[currentBatch.id].progress_percent}%</span>
                                </div>
                                <div className="progress-bar">
                                  <div
                                    className="progress-fill"
                                    style={{ width: `${batchProgress[currentBatch.id].progress_percent}%` }}
                                  />
                                </div>
                                {(batchProgress[currentBatch.id].running > 0 || batchProgress[currentBatch.id].pending > 0) && (
                                  <div className="progress-details">
                                    {batchProgress[currentBatch.id].running > 0 && (
                                      <span className="running-count">▶️ Выполняется: {batchProgress[currentBatch.id].running}</span>
                                    )}
                                    {batchProgress[currentBatch.id].pending > 0 && (
                                      <span className="pending-count">⏸ Ожидает: {batchProgress[currentBatch.id].pending}</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}

                            <div className="batch-actions">
                              {/* HACK: Разблокируем по progress_percent вместо batch.status
                                  потому что batch.status никогда не обновляется на 'completed'.
                                  TODO: Добавить фоновый task для обновления статуса батча */}
                              <button
                                onClick={() => navigate(`/pareto?batchId=${currentBatch.id}`)}
                                className="batch-action-btn analysis-btn"
                                disabled={!batchProgress[currentBatch.id] || batchProgress[currentBatch.id].progress_percent < 100}
                              >
                                📈 Анализ
                              </button>
                              <button
                                onClick={() => handleBatchDelete(currentBatch.id, portfolio.id)}
                                className="batch-action-btn delete-btn"
                              >
                                Удалить
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Модальное окно подтверждения удаления */}
      {deleteModal.show && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Подтверждение удаления</h3>
            <p>
              Вы действительно хотите удалить портфель <strong>"{deleteModal.portfolio?.name}"</strong>?
            </p>
            <p>Это действие нельзя отменить.</p>
            <div className="modal-actions">
              <button
                onClick={handleDeleteCancel}
                className="modal-btn cancel-btn"
                disabled={deleting}
              >
                Отмена
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="modal-btn delete-btn"
                disabled={deleting}
              >
                {deleting ? 'Удаляем...' : 'Удалить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
